import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cv_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const auth = {
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const billing = {
  balance: () => api.get("/billing/balance").then((r) => r.data),
  checkoutMembership: () => api.post("/billing/checkout/membership").then((r) => r.data),
  checkoutMessage: () => api.post("/billing/checkout/message").then((r) => r.data),
  portal: () => api.post("/billing/portal").then((r) => r.data),
  checkoutStatus: (sessionId) => api.get(`/billing/checkout/${sessionId}`).then((r) => r.data),
};

export const capsules = {
  list: () => api.get("/capsules").then((r) => r.data),
  get: (id) => api.get(`/capsules/${id}`).then((r) => r.data),
  create: (formData) =>
    api.post("/capsules", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  remove: (id) => api.delete(`/capsules/${id}`).then((r) => r.data),
  releaseNow: (id) => api.post(`/capsules/${id}/release-now`).then((r) => r.data),
  resendNotification: (id) => api.post(`/capsules/${id}/resend-notification`).then((r) => r.data),
  // Pending-upload flow (Feb 22, 2026): upload separately so the sender can preview
  // the actual stored file before a credit is consumed.
  uploadVideo: (file, { onProgress } = {}) => {
    const fd = new FormData();
    fd.append("video", file);
    return api
      .post("/capsules/video-uploads", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Client-Filesize": String(file.size),
        },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      })
      .then((r) => r.data);
  },
  pendingVideoBlob: (uploadId) =>
    api
      .get(`/capsules/video-uploads/${uploadId}/video`, { responseType: "blob" })
      .then((r) => r.data),
  deletePendingUpload: (uploadId) =>
    api.delete(`/capsules/video-uploads/${uploadId}`).then((r) => r.data),
};

export const publicApi = {
  meta: (token) => axios.get(`${API}/public/capsule/${token}`).then((r) => r.data),
  requestCode: (token, channel) =>
    axios.post(`${API}/public/capsule/${token}/request-code`, { channel }).then((r) => r.data),
  verifyCode: (token, code) =>
    axios.post(`${API}/public/capsule/${token}/verify-code`, { code }).then((r) => r.data),
  videoUrl: (token, viewToken) => `${API}/public/capsule/${token}/video?view=${viewToken}`,
};

export const ai = {
  draft: (data) => api.post("/ai/draft-message", data).then((r) => r.data),
  titles: (data) => api.post("/ai/suggest-titles", data).then((r) => r.data),
  rewrite: (data) => api.post("/ai/rewrite-tone", data).then((r) => r.data),
};


// Admin dashboard v1 (Feb 22, 2026) — all routes are JWT-gated by require_admin
// server-side. This client never touches the X-Admin-Token.
export const admin = {
  overview: () => api.get("/admin/overview").then((r) => r.data),
  users: (params = {}) => api.get("/admin/users", { params }).then((r) => r.data),
  user: (id) => api.get(`/admin/users/${id}`).then((r) => r.data),
  userCreditEvents: (id, params = {}) =>
    api.get(`/admin/users/${id}/credit-events`, { params }).then((r) => r.data),
  grantCredits: (id, body) =>
    api.post(`/admin/users/${id}/grant-credits`, body).then((r) => r.data),
  deductCredits: (id, body) =>
    api.post(`/admin/users/${id}/deduct-credits`, body).then((r) => r.data),
  capsules: (params = {}) => api.get("/admin/capsules", { params }).then((r) => r.data),
  capsule: (id) => api.get(`/admin/capsules/${id}`).then((r) => r.data),
  retryNotifications: (id) =>
    api.post(`/admin/capsules/${id}/retry-notifications`).then((r) => r.data),
  demoStatus: () => api.get("/admin/demo/status").then((r) => r.data),
  demoReset: () => api.post("/admin/demo/reset-session").then((r) => r.data),
  audit: (params = {}) => api.get("/admin/audit", { params }).then((r) => r.data),
  alerts: (status = "") => api.get("/admin/delivery-alerts", { params: status ? { status } : {} }).then((r) => r.data),
  alertAction: (id, action) => api.post(`/admin/delivery-alerts/${id}/${action}`).then((r) => r.data),
  recoveryBatches: () => api.get("/admin/recovery/batches").then((r) => r.data),
  createRecoveryBatch: (body) => api.post("/admin/recovery/batches", body).then((r) => r.data),
  approveRecoveryBatch: (id) => api.post(`/admin/recovery/batches/${id}/approve`, { confirm: id }).then((r) => r.data),
  runRecoveryBatch: (id) => api.post(`/admin/recovery/batches/${id}/run`).then((r) => r.data),
};
